import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, SelectF, CheckF, ImageF, inputCls } from "../admin/AFields";
import { Modal } from "../admin/Participants";

function useCrud(listUrl, saveUrl, deleteUrl) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(listUrl).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async () => {
    try { await api.post(saveUrl, editing); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try { await api.delete(`${deleteUrl}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return { items, editing, setEditing, save, remove, load };
}

export function PeopleManager() {
  const { items, editing, setEditing, save, remove } = useCrud("/studio/people", "/studio/people/save", "/studio/people");
  return (
    <div data-testid="studio-people">
      <SEO title="People" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">People / Signatory Profiles</h1>
          <p className="mt-1 text-sm text-slate-500">Photos are dynamically replaceable — changing a photo never alters already-published greetings.</p>
        </div>
        <button onClick={() => setEditing({ name: "", designation: "", designation2: "", photo: "", active: true })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="person-new-btn"><Plus size={15} /> Add Person</button>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <div key={p.id} className="rounded-lg border border-gray-200 bg-white p-5" data-testid={`person-row-${p.id}`}>
            <div className="flex items-center gap-4">
              {p.photo
                ? <img src={p.photo} alt={p.name} className="h-16 w-16 rounded-full border-2 border-saffron-500/40 object-cover" />
                : <span className="flex h-16 w-16 items-center justify-center rounded-full bg-saffron-100 font-display text-xl font-bold text-saffron-600">{p.name[0]}</span>}
              <div className="min-w-0">
                <p className="font-bold text-navy">{p.name}</p>
                <p className="text-xs text-slate-500">{p.designation}</p>
                {p.designation2 && <p className="text-xs text-slate-500">{p.designation2}</p>}
              </div>
            </div>
            <div className="mt-4 flex gap-1.5">
              <button onClick={() => setEditing(p)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`person-edit-${p.id}`}><Pencil size={13} /></button>
              <button onClick={() => remove(p.id, p.name)} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`person-del-${p.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? `Edit — ${editing.name}` : "New Person"} testid="person-editor">
          <div className="space-y-5">
            <TextF label="Full Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
            <TextF label="Designation" value={editing.designation} onChange={(v) => setEditing({ ...editing, designation: v })} placeholder="e.g. Advocate, High Court, Allahabad" />
            <TextF label="Second Designation" value={editing.designation2} onChange={(v) => setEditing({ ...editing, designation2: v })} placeholder="e.g. Chairman, Sahaj Sarathi Foundation" />
            <div>
              <ImageF label="Photo (used in future templates only)" value={editing.photo} onChange={(v) => {
                if (editing.photo && v !== editing.photo) toast.info("Use this photo for future templates? It applies to new greetings — published ones stay unchanged.");
                setEditing({ ...editing, photo: v });
              }} />
              <p className="mt-1.5 text-xs text-slate-400">To crop/reposition, prepare the square crop before upload.</p>
            </div>
            <CheckF label="Active" value={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="person-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function OrgsManager() {
  const { items, editing, setEditing, save, remove } = useCrud("/studio/orgs", "/studio/orgs/save", "/greetings/senders");
  return (
    <div data-testid="studio-orgs">
      <SEO title="Organizations" />
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Organization / Branding Profiles</h1>
        <button onClick={() => setEditing({ name: "", sender_name: "", designation: "", organisation: "", website: "", contact: "", phone: "", email: "", address: "", logo: "", footer_text: "", social: {}, enabled: true, is_default: false })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="org-new-btn"><Plus size={15} /> Add Profile</button>
      </div>
      <div className="mt-6 space-y-3">
        {items.map((o) => (
          <div key={o.id} className="rounded-lg border border-gray-200 bg-white p-5" data-testid={`org-row-${o.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-navy">{o.name} {o.is_default && <span className="ml-1 rounded-full bg-saffron-100 px-2 py-0.5 text-[10px] font-bold text-saffron-600">DEFAULT</span>} {o.enabled === false && <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">DISABLED</span>}</p>
                <p className="mt-1 text-xs text-slate-500">{o.sender_name}{o.designation ? ` · ${o.designation}` : ""} · {o.website} · {o.contact || o.phone}</p>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setEditing({ social: {}, ...o })} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`org-edit-${o.id}`}><Pencil size={13} /></button>
                <button onClick={() => remove(o.id, o.name)} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`org-del-${o.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title="Organization Profile" testid="org-editor" wide>
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Profile Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
            <TextF label="Sender Name" value={editing.sender_name} onChange={(v) => setEditing({ ...editing, sender_name: v })} />
            <TextF label="Designation" value={editing.designation} onChange={(v) => setEditing({ ...editing, designation: v })} />
            <TextF label="Organisation" value={editing.organisation} onChange={(v) => setEditing({ ...editing, organisation: v })} />
            <TextF label="Website" value={editing.website} onChange={(v) => setEditing({ ...editing, website: v })} />
            <TextF label="Contact Number" value={editing.contact} onChange={(v) => setEditing({ ...editing, contact: v })} />
            <TextF label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} />
            <TextF label="Address" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} />
          </div>
          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            {["facebook", "instagram", "youtube", "linkedin", "whatsapp"].map((k) => (
              <TextF key={k} label={`${k[0].toUpperCase() + k.slice(1)} link`} value={(editing.social || {})[k]} onChange={(v) => setEditing({ ...editing, social: { ...(editing.social || {}), [k]: v } })} />
            ))}
          </div>
          <div className="mt-5 space-y-5">
            <ImageF label="Logo" value={editing.logo} onChange={(v) => setEditing({ ...editing, logo: v })} />
            <AreaF label="Footer Text" value={editing.footer_text} onChange={(v) => setEditing({ ...editing, footer_text: v })} rows={2} />
            <div className="flex gap-6">
              <CheckF label="Enabled" value={editing.enabled !== false} onChange={(v) => setEditing({ ...editing, enabled: v })} />
              <CheckF label="Set as Default" value={!!editing.is_default} onChange={(v) => setEditing({ ...editing, is_default: v })} />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="org-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const QUOTE_CATS = ["Inspirational", "Education", "Health", "Social Service", "Legal", "National", "Festivals", "Other"];

export function QuotesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get(`/studio/quotes?q=${encodeURIComponent(q)}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    try { await api.post("/studio/quotes/save", editing); toast.success("Quote saved"); setEditing(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="studio-quotes">
      <SEO title="Quote Library" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Thought / Quote Library</h1>
          <p className="mt-1 text-sm text-slate-500">Only verified quotations are attributed to renowned persons. Unverified lines stay unattributed.</p>
        </div>
        <button onClick={() => setEditing({ text: "", author: "", category: "Inspirational", language: "english", verified: false, approved: false, active: true })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="quote-new-btn"><Plus size={15} /> Add Quote</button>
      </div>
      <div className="mt-5 flex gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search quote / author…" className={`${inputCls} !w-72`} data-testid="quotes-search" />
        <button onClick={load} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-navy" data-testid="quotes-search-btn">Search</button>
      </div>
      <div className="mt-5 space-y-3">
        {items.map((qt) => (
          <div key={qt.id} className={`rounded-lg border border-gray-200 bg-white p-5 ${qt.active === false ? "opacity-55" : ""}`} data-testid={`quote-row-${qt.id}`}>
            <p className="text-sm italic leading-relaxed text-navy">“{qt.text}”</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="font-bold text-slate-600">— {qt.author || "Original / Unattributed"}</span>
              <span className="rounded-full bg-navy-50 px-2.5 py-0.5 font-bold text-navy">{qt.category}</span>
              <span className={`rounded-full px-2.5 py-0.5 font-bold ${qt.verified ? "bg-brandgreen-100 text-brandgreen" : "bg-saffron-100 text-saffron-600"}`}>{qt.verified ? "VERIFIED" : "UNVERIFIED"}</span>
              <span className={`rounded-full px-2.5 py-0.5 font-bold ${qt.approved ? "bg-brandgreen-100 text-brandgreen" : "bg-slate-100 text-slate-500"}`}>{qt.approved ? "APPROVED" : "PENDING"}</span>
              <button onClick={() => setEditing(qt)} className="ml-auto rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`quote-edit-${qt.id}`}><Pencil size={13} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No quotes yet.</p>}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title="Quote" testid="quote-editor">
          <div className="space-y-5">
            <AreaF label="Quote / Thought" value={editing.text} onChange={(v) => setEditing({ ...editing, text: v })} rows={3} />
            <div className="grid gap-5 sm:grid-cols-2">
              <TextF label="Author (blank if original)" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} />
              <SelectF label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={QUOTE_CATS} />
              <SelectF label="Language" value={editing.language} onChange={(v) => setEditing({ ...editing, language: v })} options={["english", "hindi", "bilingual"]} />
            </div>
            <div className="flex flex-wrap gap-6">
              <CheckF label="Verified exact quotation" value={editing.verified} onChange={(v) => setEditing({ ...editing, verified: v })} />
              <CheckF label="Approved for use" value={editing.approved} onChange={(v) => setEditing({ ...editing, approved: v })} />
              <CheckF label="Active" value={editing.active} onChange={(v) => setEditing({ ...editing, active: v })} />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="quote-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export { default as CalendarManager } from "./StudioCalendar";
